import { Link } from "react-router-dom";
import { ArrowUpRight, Shapes } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import useJsonLd from "@/hooks/useJsonLd";
import ContourBackground from "@/components/ContourBackground";
import { DISCIPLINES } from "@/data/disciplines";
import { disciplineIcon } from "@/components/discipline/disciplineIcons";
import { breadcrumb } from "@/lib/jsonLd";

function DisciplineCard({ d, index }) {
  const Icon = disciplineIcon(d);
  return (
    <Link
      to={`/disciplines/${d.slug}`}
      data-testid={`discipline-card-${d.slug}`}
      className={`group relative flex flex-col justify-between border border-slate-200 bg-white p-8 hover:border-topo transition-colors ${index === 0 ? "lg:col-span-2" : ""}`}
    >
      <div>
        <div className="flex items-center justify-between">
          <span className="h-11 w-11 inline-flex items-center justify-center bg-topo text-white group-hover:bg-orange-survey transition-colors">
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-topo group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
        </div>
        <div className="mt-6 mono text-[10px] tracking-[0.22em] uppercase text-orange-survey">{d.eyebrow}</div>
        <h2 className="display mt-2 text-2xl font-extrabold tracking-tight text-slate-900 group-hover:text-topo transition-colors">{d.name}</h2>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{d.intro}</p>
      </div>
      <div className="mt-6 flex flex-wrap gap-1.5">
        {d.keywords.slice(0, 3).map((k) => (
          <span key={k} className="mono text-[9px] tracking-wide uppercase border border-slate-200 text-slate-500 px-2 py-0.5">{k}</span>
        ))}
      </div>
    </Link>
  );
}

export default function DisciplinesIndex() {
  useSeo({
    title: "BIM, MEP, Piping, Steel & Permit Drafting Services — Tri-Axis XYZ",
    description:
      "Outsourced BIM modeling, MEP BIM coordination, plant and piping drafting, structural steel and rebar detailing, and architectural permit drafting for firms in the USA, Canada and Australia.",
  });
  useJsonLd(breadcrumb([{ name: "Home", path: "/" }, { name: "Disciplines", path: "/disciplines" }]), "disciplines-bc");

  return (
    <div data-testid="page-disciplines">
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.45} seed={17} />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo inline-flex items-center gap-2">
            <Shapes className="h-3.5 w-3.5" /> Discipline hubs · Beyond survey
          </div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 max-w-4xl">
            Specialist drafting teams for <span className="text-topo">BIM, MEP, piping, steel</span> and permit sets.
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            The same production discipline our survey clients rely on, applied to building, plant and structural work. Pick a discipline to see deliverables, standards and pricing inputs.
          </p>
        </div>
      </section>
      <section className="bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="disciplines-grid">
          {DISCIPLINES.map((d, i) => <DisciplineCard key={d.slug} d={d} index={i} />)}
        </div>
      </section>
    </div>
  );
}
